// React js
import React from 'react';
import { Navigate, useRoutes } from 'react-router-dom';

// components
import { ProtectedRoute } from '@components/ProtectedRoute';

// layouts
import DashboardLayout from '@layouts/dashboard/DashboardLayout';
import NotFound from '@page/Page404';
import DashboardApp from '@page/Dashboard/index';
import DashboardV2 from '@page/DashboardV2/index';
import authRoutes from '@page/Auth/AuthRoutes';
import OriginalData from '@page/OriginalData';
import SendDownLink from '@page/SendDownLink';
import ReturnCommand from '@page/ReturnCommand';
import Devices from '@page/Devices';
import AddDevice from '@page/Devices/Add';
import Customer from '@page/Customer';
import AddCustomer from '@page/Customer/Add';
import DeviceInformationDetail from '@page/Devices/Detail';
import { Maps } from '@page/Maps';
import ReplaceDevice from '@page/Devices/Replacement';
import DetailCustomer from '@page/Customer/Detail';
import Statistics from '@page/Statistics';
import StatisticsByMonth from '@page/StatisticsByMonth';
import StatisticsByMonthV2 from '@page/StatisticsByMonthV2';
import StatisticsByMonthCnhp from '@page/StatisticsByMonthCnhp';
import Replacements from '@page/Replacements';
import AddReplacements from '@page/Replacements/Add';
import Configs from '@page/Configs';
import AddConfigs from '@page/Configs/Add';
import Warnings from '@page/Warnings';
import SendDownLinkV2 from '@page/SendDownLinkV2';
import AdjustMeterReading from '@page/AdjustMeterReading';
import CheckLeak from '@page/LeakDetector/Check';
import LeakDetectorStatistic from '@page/LeakDetector/Statistics';
import ManageUserOperator from '@page/AdminPage/ManageUserOperator';
import UserDetail from '@page/AdminPage/ManageUserOperator/components/Detail';
import MasterDataPage from '@page/MasterData';
import ManageRoles from '@page/AdminPage/ManageRoles/ManageRoles';
import ManagePermissions from '@page/AdminPage/ManagePermissions/ManagePermissions';
import RBACSeeder from '@page/AdminPage/RBACSeeder/RBACSeeder';
import ConsumptionMonitoring from '@page/ConsumptionMonitoring';
import StatisticsV2 from '@page/StatisticsV2';
import DMAPage from '@page/DMA';
import AddDMA from '@page/DMA/Add';
import DMAMonitoringPage from '@page/DMAMonitoring';
import FavoriteFilters from '@page/FavoriteFilters';
import NightMinConsumption from '@page/NightMinConsumption';
import MeterInformationPage from '@page/MeterInformation';
import SimCards from '@page/SimCards';
import AddSimCard from '@page/SimCards/Add';
import CustomerWaterConsumption from '@page/CustomerWaterConsumption';
import { CUSTOMER_WATER_CONSUMPTION_PRIVATE_PATH } from '@utils/constant';
import DeviceInspection from '@page/DeviceInspection';
import PartnerDailyDelivery from '@page/PartnerDailyDelivery';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        // { path: '/dashboard', element: <DashboardApp /> },
        { path: '/dashboard', element: <DashboardV2 /> },
        { path: '/devices', element: <Devices /> },
        { path: '/devices/add', element: <AddDevice /> },
        { path: '/device/:id', element: <AddDevice /> },
        { path: '/device/replace/:id', element: <ReplaceDevice /> },
        { path: '/device/detail/:id', element: <DeviceInformationDetail /> },
        { path: '/device/original-data', element: <OriginalData /> },
        { path: '/device/send-downlink-command', element: <SendDownLink /> },
        { path: '/device/return-command', element: <ReturnCommand /> },
        { path: '/customers', element: <Customer /> },
        { path: '/customer/add', element: <AddCustomer /> },
        { path: '/customer/:id', element: <AddCustomer /> },
        { path: '/customer/detail/:id', element: <DetailCustomer disabled={true} /> },
        { path: '/customer/consumption-statistics-by-day', element: <StatisticsV2 /> },
        { path: '/customers/consumption-statistics-by-day', element: <Statistics /> },
        // { path: '/consumption-statistics/by-month', element: <StatisticsByMonth /> },
        { path: '/customers/statistics-month', element: <StatisticsByMonthV2 /> },
        {
          path: '/customers/statistics-month-cnhp',
          element: <StatisticsByMonthCnhp />
        },
        {
          path: CUSTOMER_WATER_CONSUMPTION_PRIVATE_PATH,
          element: <CustomerWaterConsumption />
        },
        { path: '/customers/consumption-monitoring', element: <ConsumptionMonitoring /> },
        { path: '/customers/night-min-consumption', element: <NightMinConsumption /> },
        { path: '/dma', element: <DMAPage /> },
        { path: '/dma/add', element: <AddDMA /> },
        { path: '/dma/edit/:id', element: <AddDMA /> },
        { path: '/dma-monitoring', element: <DMAMonitoringPage /> },
        { path: '/customer/maps', element: <Maps /> },
        { path: '/replacements', element: <Replacements /> },
        { path: '/replacement/add', element: <AddReplacements /> },
        { path: '/replacement/update/:id', element: <AddReplacements /> },
        { path: '/replacement/detail/:id', element: <AddReplacements disabled={true} /> },
        { path: '/configs', element: <Configs /> },
        { path: '/config/add', element: <AddConfigs /> },
        { path: '/config/update/:id', element: <AddConfigs /> },
        { path: '/config/detail/:id', element: <AddConfigs disabled={true} /> },
        { path: '/warnings', element: <Warnings /> },
        { path: '/management-structure', element: <MasterDataPage /> },
        { path: '/device/check-leak', element: <CheckLeak /> },
        { path: '/device/check-leak-statistics', element: <LeakDetectorStatistic /> },
        { path: '/users', element: <ManageUserOperator /> },
        {
          path: '/user/:id',
          element: <UserDetail />
        },
        { path: '/favorite-filters', element: <FavoriteFilters /> },
        { path: '/meter-information', element: <MeterInformationPage /> },
        { path: '/roles', element: <ManageRoles /> },
        { path: '/permissions', element: <ManagePermissions /> },
        { path: '/rbac-setup', element: <RBACSeeder /> },
        { path: '/device/adjust-meter-reading', element: <AdjustMeterReading /> },
        { path: '/device/inspection-history', element: <DeviceInspection /> },
        { path: '/sim-cards', element: <SimCards /> },
        { path: '/sim-cards/add', element: <AddSimCard /> },
        { path: '/sim-cards/:id/edit', element: <AddSimCard /> },
        { path: '/partner-daily-deliveries', element: <PartnerDailyDelivery /> }
      ]
    },
    ...authRoutes,
    { path: '/', element: <Navigate to="/dashboard" /> },
    { path: '/device/send-downlink-command-v2', element: <SendDownLinkV2 /> },
    { path: '*', element: <NotFound /> }
  ]);
}
